import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import attentionStyles from "../groups/MyGroupsHero.module.css";
import styles from "./WorthALook.module.css";

/** One thing on the hero's "worth a look" list: what it is, a line on why, its figure, and where to act on it. */
export interface OrganisationInsight {
  key: string;
  title: string;
  detail: string;
  figure: string;
  /** A page to open (`/admin/users`), or a section of this page (`#id`); none when there is nowhere to act on it. */
  target?: string;
}

/**
 * The few things in the month worth a look, as My Groups lists the groups that need attention: each a row with its figure,
 * the whole row opening where it can be acted on.
 *
 * @param props.insights - The things, most important first.
 * @param props.className - Places the list in its container.
 */
export function WorthALook({ insights, className }: { insights: OrganisationInsight[]; className?: string }): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <div className={`${attentionStyles.attention} ${attentionStyles.attentionWarning} ${className ?? ""}`} role="group" aria-labelledby="organisation-worth-a-look">
      <p id="organisation-worth-a-look" className={attentionStyles.attentionTitle}>
        <svg className={attentionStyles.statusIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12.74V16h8v-1.26A7 7 0 0 0 12 2z" />
        </svg>
        {t("organisation.hero.worthALook")}
      </p>
      <ul className={`${attentionStyles.attentionList} ${styles.list}`}>
        {insights.map((insight) => {
          const linkClass = `${attentionStyles.attentionLink} ${attentionStyles.attentionItemLink}`;
          let name: React.ReactNode = insight.title;
          if (insight.target?.startsWith("#")) {
            name = (
              <a href={insight.target} className={linkClass}>
                {insight.title}
              </a>
            );
          } else if (insight.target) {
            name = (
              <Link to={insight.target} className={linkClass}>
                {insight.title}
              </Link>
            );
          }

          return (
            <li key={insight.key} className={`${attentionStyles.attentionItem} ${styles.insight} ${insight.target ? "" : styles.still}`}>
              <span className={`${attentionStyles.attentionName} ${styles.insightName}`}>
                <span className={styles.insightTitle}>{name}</span>
                <span className={attentionStyles.attentionPath}>{insight.detail}</span>
              </span>
              <span className={styles.insightFigure}>{insight.figure}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
